import {
  Box,
  Flex,
  Heading,
  Icon,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  GetIngestionDocumentFragment,
  IngestionDocumentValidationErrorReviewFragment,
  IngestionDocumentValidationFeedbackFragment,
} from '@client/graphql/__generated__/types';
import { CheckIcon, TriangleAlertIcon } from 'lucide-react';
import { FC, ReactNode } from 'react';
import { ComplianceValidationVariant } from '../ComplianceDrawer';
import { DocumentValidationFeedback } from '../DocumentValidationFeedback';
import { DocumentValidationHide } from '../DocumentValidationHide';
import { ValidationItem as ValidationItemType } from '../utils/validation.utils';
import { ValidationTooltipContent } from './ValidationTooltipContent';

interface BaseValidationItemProps {
  item: ValidationItemType;
  variant: ComplianceValidationVariant;
  type: 'error' | 'success';
  rowKey: string;
  hoveredRowIndex: string | null;
  selectedDocument: GetIngestionDocumentFragment;
  allFeedback: IngestionDocumentValidationFeedbackFragment[];
  allHiddenReviews: IngestionDocumentValidationErrorReviewFragment[];
  onItemClick: (
    mappingKeys?: string[],
    contractDocumentUuid?: string,
    isError?: boolean
  ) => void;
  onHideDocumentValidation?: (
    isHidden: boolean,
    message?: string,
    ingestionDocumentValidationId?: string,
    ruleUuid?: string,
    contractDocumentUuid?: string
  ) => void | Promise<void>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  subtitle?: ReactNode;
  tooltipContent?: ReactNode;
}

export const BaseValidationItem: FC<BaseValidationItemProps> = ({
  item,
  variant,
  type,
  selectedDocument,
  allFeedback,
  allHiddenReviews,
  onItemClick,
  onHideDocumentValidation,
  onMouseEnter,
  onMouseLeave,
  subtitle,
  tooltipContent,
}) => {
  const isError = type === 'error';
  const icon = isError ? TriangleAlertIcon : CheckIcon;

  // Check if this validation item is hidden
  const hiddenReview = allHiddenReviews.find(
    (review) =>
      review.ruleUuid === item.ruleUuid &&
      review.contractDocumentUuid === item.contractDocumentUuid &&
      review.isHidden
  );
  const isHidden = Boolean(hiddenReview);
  const hiddenMessage = hiddenReview?.message?.trim();
  const hasHiddenMessage = Boolean(hiddenMessage);

  const iconColor = isHidden
    ? 'compliance.text.muted'
    : isError
      ? 'compliance.status.error'
      : 'compliance.status.success';

  const defaultTooltipContent = (
    <ValidationTooltipContent
      inputValues={item.inputValues}
      selectedDocument={selectedDocument}
    />
  );
  const tooltipBody = (
    <VStack align="stretch" spacing={3}>
      {tooltipContent || defaultTooltipContent}
      {hasHiddenMessage && (
        <Box>
          <Heading
            color="compliance.validation.tooltip.text.primary"
            mb={2}
            size="smallUppercase"
          >
            Reason for hiding
          </Heading>
          <Text fontSize="sm">{hiddenMessage}</Text>
        </Box>
      )}
    </VStack>
  );

  return (
    <Flex
      align="center"
      gap={2}
      justify="space-between"
      opacity={isHidden ? 0.6 : 1}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Tooltip
        hasArrow
        bg="compliance.tooltip.bg"
        isDisabled={
          (!item.inputValues || item.inputValues.length === 0) &&
          !hasHiddenMessage
        }
        label={tooltipBody}
        minWidth="250px"
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: {
              rootBoundary: 'document',
            },
          },
        ]}
        variant="muted"
      >
        <Flex
          _hover={item.mappingKeys?.length ? { bg: 'compliance.hover.bg' } : {}}
          align="center"
          borderRadius="sm"
          cursor={item.mappingKeys?.length ? 'pointer' : 'default'}
          gap={2}
          justify="space-between"
          px={2}
          py={1}
          onClick={() =>
            onItemClick(item.mappingKeys, item.contractDocumentUuid, isError)
          }
        >
          <Flex align="center" gap={2}>
            <Icon as={icon} color={iconColor} flexShrink={0} />
            <Text
              color={isHidden ? 'compliance.text.muted' : undefined}
              fontSize="sm"
              fontWeight={isError ? 'medium' : 'normal'}
              textDecoration={isHidden ? 'line-through' : undefined}
            >
              {item.message}

              {subtitle && subtitle}
            </Text>
          </Flex>
        </Flex>
      </Tooltip>

      {item.ingestionDocumentValidationId &&
        item.ruleUuid &&
        item.contractDocumentUuid && (
          <>
            {variant === 'external' ? (
              <DocumentValidationHide
                isHovered
                allHiddenReviews={allHiddenReviews}
                contractDocumentUuid={item.contractDocumentUuid}
                ingestionDocumentValidationId={
                  item.ingestionDocumentValidationId
                }
                ruleUuid={item.ruleUuid}
                onHideDocumentValidation={onHideDocumentValidation}
              />
            ) : (
              <DocumentValidationFeedback
                isHovered
                allFeedback={allFeedback}
                contractDocumentUuid={item.contractDocumentUuid}
                ingestionDocumentValidationId={
                  item.ingestionDocumentValidationId
                }
                ruleUuid={item.ruleUuid}
                variant={variant}
              />
            )}
          </>
        )}
    </Flex>
  );
};
